import { useState, useMemo, useCallback } from 'react';
import { InvoicesDashboard, isInvoiceSelectable } from '@/app/features/DebtorPortal/Dashboard/dashboard.types';

export interface UseInvoiceSelectionReturn {
  selectedInvoices: Set<string>;
  allCurrentPageSelected: boolean;
  someCurrentPageSelected: boolean;
  handleSelectInvoice: (invoiceNumber: string, isSelected: boolean) => void;
  handleSelectAllOnPage: (isSelected: boolean) => void;
  handleClearAllSelections: () => void;
  getSelectedInvoiceData: (rows: InvoicesDashboard[]) => InvoicesDashboard[];
}

export const useInvoicesSelection = (
  rows: InvoicesDashboard[],
): UseInvoiceSelectionReturn => {
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(
    new Set(),
  );

  const selectableRows = useMemo(() => {
    return rows.filter((row) => isInvoiceSelectable(row));
  }, [rows]);

  const allCurrentPageSelected = useMemo(() => {
    if (selectableRows.length === 0) return false;
    return selectableRows.every((row) => selectedInvoices.has(row.invoiceNumber));
  }, [selectableRows, selectedInvoices]);

  const someCurrentPageSelected = useMemo(() => {
    if (selectableRows.length === 0) return false;
    const someSelected = selectableRows.some((row) =>
      selectedInvoices.has(row.invoiceNumber),
    );
    return someSelected && !allCurrentPageSelected;
  }, [selectableRows, selectedInvoices, allCurrentPageSelected]);

  // Selection handlers
  const handleSelectInvoice = useCallback(
    (invoiceNumber: string, isSelected: boolean) => {
      const row = rows.find((r) => r.invoiceNumber === invoiceNumber);

      if (row && !isInvoiceSelectable(row)) {
        return;
      }

      setSelectedInvoices((prev) => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.add(invoiceNumber);
        } else {
          newSet.delete(invoiceNumber);
        }
        return newSet;
      });
    },
    [rows],
  );

  const handleSelectAllOnPage = useCallback(
    (isSelected: boolean) => {
      setSelectedInvoices((prev) => {
        const newSet = new Set(prev);
        if (isSelected) {
          selectableRows.forEach((row) => newSet.add(row.invoiceNumber));
        } else {
          rows.forEach((row) => newSet.delete(row.invoiceNumber));
        }
        return newSet;
      });
    },
    [rows, selectableRows],
  );

  const handleClearAllSelections = useCallback(() => {
    setSelectedInvoices(new Set());
  }, []);

  // Get selected invoice data
  const getSelectedInvoiceData = useCallback(
    (currentRows: InvoicesDashboard[]): InvoicesDashboard[] => {
      return currentRows.filter((row) =>
        selectedInvoices.has(row.invoiceNumber),
      );
    },
    [selectedInvoices],
  );

  return {
    selectedInvoices,
    allCurrentPageSelected,
    someCurrentPageSelected,
    handleSelectInvoice,
    handleSelectAllOnPage,
    handleClearAllSelections,
    getSelectedInvoiceData,
  };
};
